import { useEffect, useState } from "react";

import { OrganizationsService } from "@/modules/dashboard/cards/services/organizationsService";
import type { Organization } from "@/modules/dashboard/cards/types/organizations-api.types";
import { HttpApiService } from "@/shared/services/httpApi.service";

/**
 * Catalog of organizations for the deposit-CLABEs org selector. Loads one page at the backend maximum (limit
 * 200 — `PaginationDto.Max(200)`) and lets `ColocaCombobox` filter client-side: the org count is far below
 * that today, and the CLABE mapping is a low-frequency ops task. `hasMore` is surfaced so the UI can disclose
 * a truncated catalog instead of silently capping (no-silent-caps rule).
 *
 * Requires `organizations:read` + INTERNAL (the route already gates on userTypes: [INTERNAL] via
 * RequireAccess); a 401/403 here means the principal cannot operate this view at all — reported as
 * `accessDenied`.
 */
export interface UseOrganizationsCatalogResult {
  organizations: Organization[];
  isLoading: boolean;
  /** True when the backend reports more orgs than the loaded page (catalog truncated). */
  isTruncated: boolean;
  hasError: boolean;
  accessDenied: boolean;
  refresh: () => void;
}

const CATALOG_LIMIT = 200;

export const useOrganizationsCatalog = (): UseOrganizationsCatalogResult => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isTruncated, setIsTruncated] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // Loading is DERIVED (attempt requested but not yet settled) — no synchronous setState in the effect.
  const [settledKey, setSettledKey] = useState<number | null>(null);
  const isLoading = settledKey !== reloadKey;

  useEffect(() => {
    let cancelled = false;
    OrganizationsService.fetchOrganizations({ limit: CATALOG_LIMIT, skip: 0 })
      .then((res) => {
        if (cancelled) return;
        setOrganizations(res.data.data);
        setIsTruncated(res.data.hasMore === true);
        setHasError(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (HttpApiService.isAccessDeniedError(err)) {
          setAccessDenied(true);
        } else {
          setHasError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setSettledKey(reloadKey);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  return {
    organizations,
    isLoading,
    isTruncated,
    hasError,
    accessDenied,
    refresh: () => setReloadKey((k) => k + 1),
  };
};
